import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Bot,
  Send
} from "lucide-react";
import { SBER, API } from "../utils/constants";

const SupportChatPage = ({ user, onBack, t }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Initial greeting
  useEffect(() => {
    setMessages([{
      id: 1,
      type: 'bot',
      text: `${t('home.greeting')}${user?.first_name ? ', ' + user.first_name : ''}! 👋\n\n${t('support.chatGreeting')}`,
      time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    }]);
  }, [user?.first_name, t]);
  
  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputText.trim(),
      time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('wallet_token') || ''}`,
          'X-Telegram-Id': user?.telegram_id?.toString() || ''
        },
        body: JSON.stringify({
          message: userMessage.text,
          session_id: sessionId
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'bot',
          text: data.response,
          time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
        }]);
      } else {
        toast.error('Ошибка отправки сообщения');
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Ошибка подключения');
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="min-h-screen flex flex-col"
      style={{ background: SBER.bgLight }}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 px-4 py-3 flex items-center gap-3" style={{ background: SBER.bgLight }}>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onBack}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
        >
          <ArrowLeft size={20} style={{ color: SBER.text }} />
        </motion.button>
        <div className="flex-1">
          <h1 className="text-lg font-bold" style={{ color: SBER.text }}>Поддержка</h1>
          <p className="text-xs" style={{ color: SBER.gray }}>AI-ассистент</p>
        </div>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: SBER.green }}>
          <Bot size={20} color="white" />
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                msg.type === 'user' 
                  ? 'rounded-br-md' 
                  : 'rounded-bl-md'
              }`}
              style={{
                background: msg.type === 'user' ? SBER.green : 'white',
                color: msg.type === 'user' ? 'white' : SBER.text
              }}
            >
              <p className="text-[15px] whitespace-pre-wrap">{msg.text}</p>
              <p 
                className="text-[11px] mt-1 text-right"
                style={{ opacity: 0.7 }}
              >
                {msg.time}
              </p>
            </div>
          </motion.div>
        ))}
        
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div 
              className="px-4 py-3 rounded-2xl rounded-bl-md"
              style={{ background: 'white' }}
            >
              <div className="flex gap-1">
                <motion.div
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-2 h-2 rounded-full"
                  style={{ background: SBER.gray }}
                />
                <motion.div
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                  className="w-2 h-2 rounded-full"
                  style={{ background: SBER.gray }}
                />
                <motion.div
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                  className="w-2 h-2 rounded-full"
                  style={{ background: SBER.gray }}
                />
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="sticky bottom-0 p-4" style={{ background: SBER.bgLight }}>
        <div 
          className="flex items-end gap-2 p-2 rounded-2xl"
          style={{ background: 'white', boxShadow: '0 -2px 10px rgba(0,0,0,0.05)' }}
        >
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Напишите сообщение..."
            className="flex-1 resize-none outline-none text-[15px] max-h-32 p-2"
            style={{ color: SBER.text }}
            rows={1}
            data-testid="chat-input"
          />
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={sendMessage}
            disabled={!inputText.trim() || loading}
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ 
              background: inputText.trim() && !loading ? SBER.green : SBER.grayPale,
              transition: 'background 0.2s'
            }}
            data-testid="chat-send-btn"
          >
            <Send size={18} color={inputText.trim() && !loading ? 'white' : SBER.gray} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default SupportChatPage;
